if (!customElements.get('product-countdown')) {
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const FOUR_DAYS = 4 * DAY;

  const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
  const LEADING_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

  /**
   * Europe/Copenhagen's UTC offset, in minutes, on a given calendar date.
   *
   * The AX schedule's campaignEndDate is a date with no time, so "the end of the
   * campaign day" has to be anchored to the shop's timezone — otherwise the final
   * hours of an offer tick down at the wrong moment for a visitor abroad, and the
   * line disappears on the wrong side of midnight.
   *
   * @param {number} year
   * @param {number} month - 1-12.
   * @param {number} day
   * @returns {number} Minutes ahead of UTC (60 for CET, 120 for CEST).
   */
  function copenhagenOffsetMinutes(year, month, day) {
    // Noon UTC is never near a DST switch, so the wall clock it maps to in
    // Copenhagen always belongs to the intended calendar day.
    const noonUtc = Date.UTC(year, month - 1, day, 12, 0, 0);

    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Copenhagen',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).formatToParts(new Date(noonUtc));

      const wall = {};
      parts.forEach((part) => { wall[part.type] = part.value; });

      const asIfUtc = Date.UTC(
        Number(wall.year),
        Number(wall.month) - 1,
        Number(wall.day),
        Number(wall.hour) % 24,
        Number(wall.minute)
      );

      return Math.round((asIfUtc - noonUtc) / MINUTE);
    } catch (e) {
      // DST-naive fallback for engines without a full ICU build: CEST covers
      // April-October in full, and the March/October switch weeks are the only
      // dates this gets wrong by an hour.
      return month >= 4 && month <= 10 ? 120 : 60;
    }
  }

  /**
   * Resolves an end-date string to an epoch timestamp.
   *
   * A bare `YYYY-MM-DD` (the AX campaignEndDate) means the offer runs THROUGH
   * that day, so it expires at 23:59:59 Europe/Copenhagen. A value carrying a
   * time (the merchant metafield, rendered with its own offset) is an instant the
   * merchant picked and is used verbatim.
   *
   * @param {string} value
   * @returns {number} Epoch ms, or NaN when unusable.
   */
  function resolveEndDate(value) {
    const raw = String(value || '').trim();
    if (raw === '') return NaN;

    const dateOnly = raw.match(DATE_ONLY);
    if (dateOnly) {
      const year = Number(dateOnly[1]);
      const month = Number(dateOnly[2]);
      const day = Number(dateOnly[3]);
      const endOfDayUtc = Date.UTC(year, month - 1, day, 23, 59, 59);
      return endOfDayUtc - (copenhagenOffsetMinutes(year, month, day) * MINUTE);
    }

    const parsed = new Date(raw).getTime();
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  /**
   * Compact three-band countdown for the product page price block, shared by both
   * end-date sources (see _countdown-bands.liquid). Bands: more than 4 days out
   * shows the end date, 24h to 4 days shows whole days, under 24h counts down
   * live. Hides itself once the deadline passes or when it has no date at all.
   */
  class ProductCountdown extends HTMLElement {
    connectedCallback() {
      this.applyEndDate(this.dataset.endDate);
    }

    disconnectedCallback() {
      this.stop();
    }

    /**
     * Points the countdown at a new deadline, or clears it. The schedule-driven
     * host resolves a different campaign per variant and per day, so starting
     * over has to be repeatable — not a one-shot connectedCallback.
     *
     * @param {string} value - ISO datetime, a `YYYY-MM-DD` date, or '' to hide.
     */
    setEndDate(value) {
      this.dataset.endDate = value || '';
      this.applyEndDate(value);
    }

    /** @param {string} value */
    applyEndDate(value) {
      this.stop();
      this.endDate = resolveEndDate(value);
      // The date band prints the shop-local calendar day, taken from the source
      // string rather than the resolved timestamp — formatting 23:59 Copenhagen
      // in a visitor's own timezone can roll it to the next day.
      const leadingDate = String(value || '').match(LEADING_DATE);
      this.endDateLabel = leadingDate ? `${leadingDate[3]} / ${leadingDate[2]} / ${leadingDate[1]}` : '';

      if (!Number.isFinite(this.endDate) || this.endDate <= Date.now()) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      // Already wired up — a variant swap re-pointing the same element just needs
      // the run restarted, not a second IntersectionObserver.
      if (this.labelEl) {
        this.init();
        return;
      }
      if (this.lazyPending) return;
      this.lazyPending = true;
      window.initLazyScript(this, () => {
        this.lazyPending = false;
        this.init();
      });
    }

    stop() {
      clearInterval(this.interval);
      this.interval = null;
    }

    init() {
      if (!this.labelEl) {
        this.labelEl = this.querySelector('.js-pc-label');
        this.dateEl = this.querySelector('.js-pc-date');
        this.daysEl = this.querySelector('.js-pc-days');
        this.daysCountEl = this.querySelector('.js-pc-days-count');
        this.daysUnitEl = this.querySelector('.js-pc-days-unit');
        this.clockEl = this.querySelector('.js-pc-clock');
        this.hoursEl = this.querySelector('.js-pc-hours');
        this.minsEl = this.querySelector('.js-pc-mins');
        this.secsEl = this.querySelector('.js-pc-secs');

        this.labelExpires = this.dataset.labelExpires;
        this.labelEnds = this.dataset.labelEnds;
        this.labelMemberExpires = this.dataset.labelMemberExpires;
        this.labelMemberEnds = this.dataset.labelMemberEnds;
        this.daySingular = this.dataset.daySingular;
        this.dayPlural = this.dataset.dayPlural;
      }

      // initLazyScript may fire after setEndDate() already started a run, and a
      // variant change can call setEndDate() twice — never stack intervals.
      this.stop();
      if (!Number.isFinite(this.endDate)) return;

      this.tick();
      this.interval = setInterval(this.tick.bind(this), SECOND);
    }

    /**
     * The label pair for the price story currently on screen. Read off the
     * attribute on every tick rather than cached alongside the strings in
     * init(): <pdp-price-schedule> (main.js) resolves the real
     * Europe/Copenhagen "today" client-side and can flip a variant between a
     * member day and a campaign day well after this element was upgraded.
     *
     * Falls back to the sale wording if a host set data-member without
     * emitting the member strings — a missing label is worse than the wrong one.
     *
     * @returns {{ends: string, expires: string}}
     */
    get labels() {
      if (this.dataset.member === 'true') {
        return {
          ends: this.labelMemberEnds || this.labelEnds,
          expires: this.labelMemberExpires || this.labelExpires
        };
      }
      return { ends: this.labelEnds, expires: this.labelExpires };
    }

    /** Show one of the three value blocks, hide the others. */
    setView(view) {
      this.dateEl.hidden = view !== 'date';
      this.daysEl.hidden = view !== 'days';
      this.clockEl.hidden = view !== 'clock';
    }

    tick() {
      const diff = this.endDate - Date.now();

      if (diff < 0) {
        this.stop();
        this.hidden = true;
        return;
      }

      // The accessible name follows the member flag, so a member day is never
      // announced as an offer either. It deliberately keeps the "expires in"
      // phrasing in all three bands, as it did before this was member-aware —
      // the band-vs-name wording is a separate, pre-existing question.
      // Guarded: rewriting an unchanged attribute once a second re-announces
      // the timer.
      const labels = this.labels;
      if (this.getAttribute('aria-label') !== labels.expires) {
        this.setAttribute('aria-label', labels.expires);
      }

      if (diff > FOUR_DAYS) {
        // More than 4 days out: show the end date.
        this.labelEl.textContent = labels.ends;
        if (this.endDateLabel) this.dateEl.textContent = this.endDateLabel;
        this.setView('date');
        return;
      }

      if (diff >= DAY) {
        // 24h to 4 days: show whole days remaining (floored, never 0 in this band).
        const days = Math.floor(diff / DAY);
        this.labelEl.textContent = labels.expires;
        this.daysCountEl.textContent = days;
        this.daysUnitEl.textContent = days === 1 ? this.daySingular : this.dayPlural;
        this.setView('days');
        return;
      }

      // Under 24h: live HH : MM : SS.
      const pad = (n) => String(n).padStart(2, '0');
      const totalHours = Math.floor(diff / HOUR);
      const mins = Math.floor((diff % HOUR) / MINUTE);
      const secs = Math.floor((diff % MINUTE) / SECOND);

      this.labelEl.textContent = labels.expires;
      this.hoursEl.textContent = pad(totalHours);
      this.minsEl.textContent = pad(mins);
      this.secsEl.textContent = pad(secs);
      this.setView('clock');
    }
  }

  customElements.define('product-countdown', ProductCountdown);
}
